import { create } from 'zustand';
import type { JournalEntry, DbJournalEntry, JournalSource } from '@machina/core';
import { dbToJournalEntry } from '@machina/core';
import { supabase } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';

// =============================================================================
// STORE
// =============================================================================

interface JournalState {
  entries: JournalEntry[];
  campaignId: string | null;
  realtimeCleanup: (() => void) | null;

  loadJournal: (campaignId: string) => Promise<void>;
  addEntry: (params: {
    campaignId: string;
    source: JournalSource;
    title?: string;
    body: string;
    sceneNames?: string[];
    sessionPlanId?: string;
  }) => Promise<JournalEntry | null>;
  updateEntry: (id: string, updates: { title?: string; body?: string; sceneNames?: string[] }) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;
}

export const useJournalStore = create<JournalState>((set, get) => ({
  entries: [],
  campaignId: null,
  realtimeCleanup: null,

  loadJournal: async (campaignId) => {
    set({ campaignId });
    try {
      const { data, error } = await supabase
        .from('machina_campaign_journal')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('[journalStore] Failed to load journal:', error);
        return;
      }

      const entries = (data as unknown as DbJournalEntry[]).map(dbToJournalEntry);
      set({ entries });
      console.log(`[journalStore] Loaded ${entries.length} journal entries`);
    } catch (err) {
      console.warn('[journalStore] Failed to load journal:', err);
    }
  },

  addEntry: async ({ campaignId, source, title, body, sceneNames, sessionPlanId }) => {
    try {
      const { data: inserted, error } = await supabase
        .from('machina_campaign_journal')
        .insert({
          campaign_id: campaignId,
          source,
          title: title ?? null,
          body,
          scene_names: sceneNames ?? [],
          session_plan_id: sessionPlanId ?? null,
        })
        .select()
        .single();

      if (error) {
        console.warn('[journalStore] Failed to insert entry:', error);
        return null;
      }

      const entry = dbToJournalEntry(inserted as unknown as DbJournalEntry);
      set((state) => ({ entries: [entry, ...state.entries] }));
      return entry;
    } catch (err) {
      console.warn('[journalStore] Failed to insert entry:', err);
      return null;
    }
  },

  updateEntry: async (id, updates) => {
    try {
      const dbUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.body !== undefined) dbUpdates.body = updates.body;
      if (updates.sceneNames !== undefined) dbUpdates.scene_names = updates.sceneNames;

      const { data: updated, error } = await supabase
        .from('machina_campaign_journal')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.warn('[journalStore] Failed to update entry:', error);
        return;
      }

      const entry = dbToJournalEntry(updated as unknown as DbJournalEntry);
      set((state) => ({
        entries: state.entries.map((e) => (e.id === id ? entry : e)),
      }));
    } catch (err) {
      console.warn('[journalStore] Failed to update entry:', err);
    }
  },

  deleteEntry: async (id) => {
    try {
      const { error } = await supabase
        .from('machina_campaign_journal')
        .delete()
        .eq('id', id);

      if (error) {
        console.warn('[journalStore] Failed to delete entry:', error);
        return;
      }

      set((state) => ({ entries: state.entries.filter((e) => e.id !== id) }));
    } catch (err) {
      console.warn('[journalStore] Failed to delete entry:', err);
    }
  },

  subscribeToRealtime: (campaignId) => {
    get().unsubscribeFromRealtime();
    set({ campaignId });

    const { cleanup } = createSubscription({
      channelName: `campaign-journal:${campaignId}`,
      table: 'machina_campaign_journal',
      filter: `campaign_id=eq.${campaignId}`,
      event: '*',
      onPayload: (payload) => {
        if (payload.eventType === 'INSERT') {
          const db = payload.new as unknown as DbJournalEntry;
          const entry = dbToJournalEntry(db);
          set((state) => {
            if (state.entries.some((e) => e.id === entry.id)) return state;
            return { entries: [entry, ...state.entries] };
          });
        } else if (payload.eventType === 'UPDATE') {
          const db = payload.new as unknown as DbJournalEntry;
          const entry = dbToJournalEntry(db);
          set((state) => ({
            entries: state.entries.map((e) => (e.id === entry.id ? entry : e)),
          }));
        } else if (payload.eventType === 'DELETE') {
          const deletedId = (payload.old as { id?: string }).id;
          if (deletedId) {
            set((state) => ({ entries: state.entries.filter((e) => e.id !== deletedId) }));
          }
        }
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
